import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface AnimatedProgressProps {
  value: number;
  max?: number;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  colorByProgress?: boolean;
}

export function AnimatedProgress({
  value,
  max = 100,
  className,
  size = 'md',
  showLabel = false,
  colorByProgress = true,
}: AnimatedProgressProps) {
  const [animatedValue, setAnimatedValue] = useState(0);
  const percentage = Math.min((value / max) * 100, 100);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValue(percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  const getProgressColor = () => {
    if (!colorByProgress) return 'bg-primary';
    if (percentage >= 100) return 'bg-success';
    if (percentage >= 75) return 'bg-primary';
    if (percentage >= 50) return 'bg-warning';
    return 'bg-destructive/70';
  };

  const getGlowEffect = () => {
    if (percentage >= 100) return 'shadow-[0_0_20px_hsl(var(--success)/0.5)]';
    if (percentage >= 75) return 'shadow-[0_0_15px_hsl(var(--primary)/0.3)]';
    return '';
  };

  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4',
  };

  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          'w-full rounded-full bg-muted overflow-hidden',
          sizeClasses[size]
        )}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-700 ease-out',
            getProgressColor(),
            getGlowEffect(),
            percentage >= 100 && 'animate-pulse'
          )}
          style={{ width: `${animatedValue}%` }}
        />
      </div>
      {showLabel && (
        <div className="flex justify-between mt-1 text-xs text-muted-foreground">
          <span>{Math.round(percentage)}%</span>
          {percentage >= 100 && (
            <span className="text-success font-medium">Meta atingida! 🎉</span>
          )}
        </div>
      )}
    </div>
  );
}
